import React, {Component, Fragment} from 'react';
import { connect } from "react-redux";
import { IRootState } from "../../../store";
import DispatchProps from "../../../utils/DispatchProps";
import School, { License, OnboardStatus } from "../../../models/School";
import {
    CommandBarButton,
    Dropdown,
    MessageBarType,
    TextField,
    PrimaryButton,
    IDropdownOption,
    Toggle, Dialog, DialogType, DialogFooter, DefaultButton,
} from "office-ui-fabric-react";
import Actions from "../../../redux/Actions";
import ApiService from "../../ApiService";
import H from "history";
import PageTitle from "../../PageTitle";
import UpdateSchoolLicense from '../../UpdateSchoolLicense';
import UpdateSchoolCredentials from "../../UpdateSchoolCredentials";
import {UserAccessLevel, userAccessLevelIsAtLeast} from '../../../models/UserAccessRecord';
import AdminSchoolMenu from "../../AdminSchoolMenu";
import moment from "moment";
import {SchoolDeleteSettings} from "../../SchoolDeleteSettings";
import LoadingPage from "../../LoadingPage";
import SchoolPageTitle from '../../SchoolPageTitle';
import AssignSchoolLicense from "../../AssignSchoolLicense";
import ForceOnboardStatus from '../../ForceOnboardStatus';

interface UpdateSchoolPageProps {
    tenantId: string;
    schoolCode: string;
    
    usersCdbAccessLevel?: UserAccessLevel,

    apiService: ApiService;
    history: H.History;
}

interface UpdateSchoolPageState {
    [key: string]: any;

    isNewSchool?: boolean;
    school?: School,
    
    saveButtonDisabled: boolean;
    logo?: File;

    schoolDeleteSettings?: SchoolDeleteSettings;
    
    salamanderPasswordHash: string | undefined;
    
    updateLicenseMethod: "update" | "assign";
}

const ADMIN_ONLY_CLIENT_ID_OPTIONS: IDropdownOption[] = [
    {
        key: "d41f885a-1809-40f2-9b7f-d41db67c447e",
        text: "Workloads APringleDev (dev) (Visible to CDBAdmins only)"
    },
];

const CLIENT_ID_DROPDOWN_OPTIONS: IDropdownOption[] = [
    {
        key: "27bbceb3-9ad9-4aff-98e1-179c8dbb8e11",
        text: "Workloads Prod (new) (Use for new builds)"
    },
    {
        key: "202b81a8-6fc9-4c14-8d3e-39c8309b17d5",
        text: "Workloads Dev (dev) (Dev use only)"
    }
];


class UpdateSchoolPage extends Component<UpdateSchoolPageProps & DispatchProps, UpdateSchoolPageState> {
    static displayName = UpdateSchoolPage.name;

    public static createSchoolFormField(component: any, statePropertyName: string, valuePropertyName: string, label: string, opts: any): JSX.Element {

        const currentFieldValue = component.state[statePropertyName][valuePropertyName];

        return (<TextField className="card__input" label={label} {...opts} value={currentFieldValue ? currentFieldValue : ""} onChange={(event: any) => {

            const updatedStateProperty = Object.assign({}, component.state[statePropertyName]);
            updatedStateProperty[valuePropertyName] = event.target.value;


            const stateUpdate: any = {};
            stateUpdate[statePropertyName] = updatedStateProperty;

            component.setState(stateUpdate);
        }} />);
    }

    constructor(props: UpdateSchoolPageProps & DispatchProps) {
        super(props);

        this.state = {
            school: undefined,
            isNewSchool: false,
            saveButtonDisabled: false,
            updateLicenseMethod: "assign",
            salamanderPasswordHash: undefined
        };

    }

    async componentDidMount() {
        
        const schoolDeleteSettings = await this.props.apiService.getSchoolDeleteSettingsAdmin();
        this.setState({
            schoolDeleteSettings: schoolDeleteSettings
        });
        
        await this.loadSchoolOrNew();
    }

    async componentDidUpdate(prevProps: Readonly<UpdateSchoolPageProps & DispatchProps>, prevState: UpdateSchoolPageState): Promise<void> {
        
        if (this.props.tenantId !== prevProps.tenantId || this.props.schoolCode !== prevProps.schoolCode)
        {
            await this.loadSchoolOrNew();
        }
    }
    
    private async loadSchoolOrNew(): Promise<void> 
    {
        if(this.props.tenantId === undefined && this.props.schoolCode === undefined)
        {
            this.setState({
                isNewSchool: true,
                school:  { onboardStatus: "completed" as OnboardStatus, license: "unlicensed" as License, clientId: CLIENT_ID_DROPDOWN_OPTIONS[0].key as string }
            })
        }
        else
        {
            const school = await this.props.apiService.getSchoolAdmin(this.props.tenantId, this.props.schoolCode, true);
            const updateLicenseMethod = await this.props.apiService.getLicenseUpdateMethod(this.props.tenantId);
            this.setState({
                isNewSchool: false,
                school: school,
                updateLicenseMethod: updateLicenseMethod
            });
        }
    }

    protected submitForm = async () => {
        
        if(!this.state.school)
        {
            return;
        }
        
        try {
            this.setState({ saveButtonDisabled: true });

            const savedSchool = await this.props.apiService.updateSchoolAdmin(this.state.school);

            if (savedSchool.tenantId != null && savedSchool.schoolCode != null && this.state.logo != null) {
                await this.props.apiService.updateSchoolLogoAdmin(savedSchool.tenantId, savedSchool.schoolCode, this.state.logo);
            }

            this.props.dispatch(Actions.showMessageBar("School saved", MessageBarType.success));

            this.setState({ saveButtonDisabled: false });

            await this.loadSchoolOrNew();

            this.props.history.push(`/cdb-admin/schools/${savedSchool.tenantId}/${savedSchool.schoolCode}`);
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Failed to save school : ${e.message}`, MessageBarType.error));
            this.setState({ saveButtonDisabled: false });
        }
    };
    

    private createFormFields(): JSX.Element {
        
        let clientIdDropdownOptions = CLIENT_ID_DROPDOWN_OPTIONS;
        if(this.props.usersCdbAccessLevel && userAccessLevelIsAtLeast("admin",this.props.usersCdbAccessLevel))
        {
            clientIdDropdownOptions = [...CLIENT_ID_DROPDOWN_OPTIONS,...ADMIN_ONLY_CLIENT_ID_OPTIONS];
        }
        return (
            <Fragment>
                {UpdateSchoolPage.createSchoolFormField(this, "school", "tenantId", "Tenant Id", {
                    disabled: true,
                    required: false,
                    description: "This will be looked up automatically using the tenant name below"
                })}
                {UpdateSchoolPage.createSchoolFormField(this, "school", "tenantName", "Tenant Name", {
                    disabled: !this.state.isNewSchool,
                    required: true,
                    description: "Usually the subdomain the customer's SharePoint uses. Such as : TENANTNAME.sharepoint.com"
                })}
                {UpdateSchoolPage.createSchoolFormField(this, "school", "schoolCode", "School Code", {
                    disabled: !this.state.isNewSchool,
                    required: true,
                    description: "Short and unique letters-only code to identify the school, will be visible to CDB and customers"
                })}
                {UpdateSchoolPage.createSchoolFormField(this, "school", "schoolName", "School Name", {
                    disabled: false,
                    required: false,
                    description: "Will be visible to CDB and customers"
                })}
                {UpdateSchoolPage.createSchoolFormField(this, "school", "sdsSchoolId", "SDS School Id", {
                    disabled: false,
                    required: true,
                    description: "Usually the DFE number of the school with slashes replaced, such as : 999-9999"
                })}

                
                <TextField className="card__input file-input"
                           value={this.state.logo?.name}
                           label="Logo"
                           description="If blank logo will not be changed"
                           readOnly onRenderPrefix={() => {
                    return <PrimaryButton>
                        <input id="logo-file"
                               type="file"
                               accept="image/png, image/jpeg, image/gif, image/svg+xml"
                               onChange={(e) => {
                                   if (e.target.files?.length === 1) {
                                       const file = e.target.files[0];
                                       this.setState({
                                           logo: file
                                       })
                                   }
                               }}
                        />
                        <label htmlFor="logo-file">Choose a file</label>
                    </PrimaryButton>
                }}/>

                {UpdateSchoolPage.createSchoolFormField(this, "school", "onboardStatus", "Onboarding Status", {
                    disabled: true,
                    required: false,
                    description: "How far this school is through the onboarding process"
                })}
                

                <br></br>

                <Toggle
                    label={"Third Party SDS"}
                    disabled={this.state.school === undefined}
                    checked={this.state.school?.thirdPartySDS}
                    inlineLabel
                    onChange={(e, checked) => {
                        const school = this.state.school!;
                        school.thirdPartySDS = checked;
                        this.setState(school);
                    }}
                />

                <h4>Advanced (leave as default if unsure)</h4>
                <Dropdown
                    disabled={this.state.school === undefined}
                    selectedKey={this.state.school?.clientId}
                    label={"App Registration for Workloads"}
                    placeholder={"Leave blank for default"}
                    options={clientIdDropdownOptions}
                    onChange={(e, option) => {
                        if (option) {
                            const school = Object.assign({}, this.state.school, {clientId: option.key});
                            this.setState({
                                school
                            });
                        }
                    }}
                />

            </Fragment>);
    }
    
    private updateSchoolLicense() {
        if (this.props.tenantId && this.props.schoolCode) {
            return <UpdateSchoolLicense
                schoolCode={this.props.schoolCode}
                tenantId={this.props.tenantId} />
        }
    }

    private assignSchoolLicense() {
        if (this.props.tenantId && this.props.schoolCode) {
            return <AssignSchoolLicense
                schoolCode={this.props.schoolCode}
                tenantId={this.props.tenantId} />
        }
    }

    private updateSchoolCreds() {
        if (this.props.tenantId && this.props.schoolCode && this.state.school) {
            return <UpdateSchoolCredentials school={this.state.school} />;
        }
    }

    private salamanderSchoolConfiguration() {
        if (this.props.usersCdbAccessLevel == "Engineer" || "Admin") {
            return (
                <div className="card card">
                    <div className="card__header">
                        <span className={"card__title"}>Salamander Password</span>
                        <CommandBarButton
                            iconProps={{ iconName: "unlock" }}
                            onClick={() => this.onGenerateNewSalamanderPassword()}
                            text="Generate"
                        />
                    </div>
                    <div className="card__content flex-column">
                        {this.state.salamanderPasswordHash != undefined ?
                            <div>
                                <div style={{fontWeight: "bold", marginBottom: "10px" }}>
                                    {this.state.salamanderPasswordHash}
                                </div>
                                <div>
                                    Created/Updated Salamander School Record. Password can now be used in Salamander WebServiceActivity.
                                </div>
                            </div> : null}
                    </div>
                </div>);
        }
        else {
            return "";
        }
    }

    private async onGenerateNewSalamanderPassword() {
        try {
            this.setState({ salamanderPasswordHash: undefined });
            var hash = await this.props.apiService.generateNewSalamanderPassword(this.props.tenantId, this.props.schoolCode);
            this.setState({ salamanderPasswordHash: hash });
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar("Unable to Generate new Password Hash", MessageBarType.error));
        }
    }

    private async onDeleteClicked() {
        this.setState({ isDeleting: true });
        try {
            await this.props.apiService.deleteSchoolAdmin(this.props.tenantId, this.props.schoolCode);
            this.props.dispatch(Actions.showMessageBar("successfully deleted school", MessageBarType.success));
            this.props.history.push(`/cdb-admin/schools`);
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Failed to delete school`, MessageBarType.error))
        }
        this.setState({ isDeleting: false });
    }

    private getDeleteDialogText(): string {

        return this.canDeleteSchool() ?
            'Are you sure you want to delete this school?' :
            `Cannot delete school unless it has been disabled for more than ${this.state.schoolDeleteSettings?.canDeleteAfterDisabledDays} Days`;
    }

    private canDeleteSchool(): boolean {
        if (this.state.school && this.state.schoolDeleteSettings && this.state.school.lastDisabledOn) {
            var disabledTime = moment(this.state.school.lastDisabledOn);
            var notMinusSevenDaysTime = moment().subtract(this.state.schoolDeleteSettings.canDeleteAfterDisabledDays, 'days');
            return disabledTime.isBefore(notMinusSevenDaysTime);
        }
        return false;
    }
    
    
    private renderExistingSchoolButtons(): JSX.Element | undefined
    {
        if(this.state.isNewSchool)
        {
            return undefined;
        }
        
        const enableButton = (
            <CommandBarButton
                iconProps={{ iconName: "accept" }}
                text="Enable"
                onClick={async () => {
                    const school = await this.props.apiService.enableSchoolAdmin(this.props.tenantId, this.props.schoolCode);
                    this.setState({
                        school
                    });
                    this.props.dispatch(Actions.showMessageBar("Successfully enabled school", MessageBarType.success));
                }} />
        );


        const disableButton = (
            <CommandBarButton
                iconProps={{ iconName: "cancel" }}
                text="Disable"
                onClick={async () => {
                    const school = await this.props.apiService.disableSchoolAdmin(this.props.tenantId, this.props.schoolCode);
                    this.setState({
                        school
                    });
                    this.props.dispatch(Actions.showMessageBar("Successfully disabled school", MessageBarType.success));
                }} />
        );

        const deleteButton = (
            <Fragment>
                <CommandBarButton
                    iconProps={{ iconName: "delete" }}
                    text="Delete"
                    onClick={() => this.setState({ showDeleteDialog: true })} />
                <Dialog
                    hidden={!this.state.showDeleteDialog}
                    onDismiss={() => this.setState({ showDeleteDialog: false })}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: 'Delete School',
                        closeButtonAriaLabel: 'Close',
                        subText: this.getDeleteDialogText(),
                    }}>
                    <DialogFooter>
                        <PrimaryButton
                            onClick={() => this.onDeleteClicked()}
                            text="Delete"
                            disabled={!this.canDeleteSchool() || this.state.isDeleting} />
                        <DefaultButton
                            onClick={() => this.setState({ showDeleteDialog: false })}
                            text="Cancel" />
                    </DialogFooter>
                </Dialog>
            </Fragment>
        );
        
        
        return (
            <Fragment>
                {this.state.school?.disabled ? enableButton : disableButton}
                {this.state.school?.disabled ? deleteButton : ""}
            </Fragment>
        );

    }

    private getPageTitle(): JSX.Element {
        if (this.state.isNewSchool) {
            return <PageTitle title="Add School" />;
        }
        else {
            return <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Edit" /> 
        }
    }

    render() {
        if(this.state.school === undefined)
        {
            return <LoadingPage/>;
        }
        
        const title = this.state.isNewSchool ? "Add School" : `Edit ${this.state.school?.schoolName}`;

        return (
            <Fragment>
                {this.getPageTitle()}

                {this.state.isNewSchool ? null : <AdminSchoolMenu tenantId={this.props.tenantId} schoolCode={this.props.schoolCode}/>}
                
                <div className="card card">
                    <div className="card__header">

                        <span className={"card__title"}>{title}</span>
                        
                        {this.renderExistingSchoolButtons()}                        

                        <CommandBarButton
                            iconProps={{ iconName: "save" }}
                            text="Save"
                            disabled={this.state.saveButtonDisabled}
                            onClick={this.submitForm}
                        />
                    </div>

                    <div className="card__content flex-column">
                        {this.state.school ? this.createFormFields() : null}
                    </div>

                </div>
                {this.updateSchoolCreds()}
                {!this.state.isNewSchool && this.state.updateLicenseMethod == "update" ? this.updateSchoolLicense() : null}
                {!this.state.isNewSchool && this.state.updateLicenseMethod == "assign" ? this.assignSchoolLicense() : null}
                {this.salamanderSchoolConfiguration()}
                <ForceOnboardStatus tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} />
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: any): UpdateSchoolPageProps => {

        const tenantId = ownProps.match.params.tenantId;
        const schoolCode = ownProps.match.params.schoolCode;
        
        return {
            tenantId,
            schoolCode,
            
            usersCdbAccessLevel: state.auth.usersCdbAccessLevel,
            apiService: state.auth.apiService,
            history: ownProps.history,
        };
    }
)(UpdateSchoolPage)